import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import PageShell from '../layout/PageShell';
import ActivationSummary from '../components/ActivationSummary';
import { useSession } from '../hooks/useSession';
import {
  apiErrorStatus,
  fetchPaymentStatus,
  type PaymentStatus,
  type PaymentStatusResponse,
} from '../lib/api';
import { formatCents } from '../lib/format';

/**
 * Where a customer lands after SaturnShift's hosted checkout.
 *
 * ===============================================================================
 * THE REDIRECT PROVES NOTHING, AND THIS PAGE IS BUILT AROUND THAT.
 * ===============================================================================
 *
 * SaturnShift appends no payment identifiers to the return URL and hands the browser no
 * transaction id, no callback and no success signal — so arriving here means a browser
 * followed a redirect, and nothing more. It does not mean the customer paid, and a
 * customer who abandoned checkout can reach this URL by pressing Back.
 *
 * So this page renders NOTHING of its own about the outcome. It asks the server, and
 * shows what the server says. The word "confirmed" appears only for `status === 'paid'`,
 * which the backend sets only after a verified webhook or an authenticated, server-side
 * reconciliation recorded the payment.
 *
 * ---------------------------------------------------------------------------
 * ONE REQUEST ON MOUNT. NO TIMER, EVER.
 *
 * There is no interval, no retry loop and no exponential backoff. A pending order causes
 * the BACKEND to reconcile against SaturnShift exactly once per request, and a poll here
 * would be a poll against SaturnShift — which is the thing the whole design avoids. If
 * the answer is not final, the customer is told they can close the page and that we will
 * confirm by email, and given a button they can press themselves.
 *
 * "CHECK AGAIN" IS A READ. It calls this one endpoint a second time. It does not create
 * an order, does not re-open the checkout widget, and does not touch the idempotency key
 * — retrying the CHECKOUT is a different button on a different page (Configure), and
 * conflating the two is how a customer ends up with two orders.
 *
 * ---------------------------------------------------------------------------
 * NOT BEHIND A SESSION, DELIBERATELY. A customer coming back from paying has not
 * necessarily signed in, and demanding it here would lock them out of the confirmation for
 * the payment they just made. The order number in the URL is what identifies the order,
 * and the endpoint answers with the small public DTO — no email, no payout address, no
 * pricing internals.
 */

/*
 * ===========================================================================
 * HOW OFTEN THE CONFIRMING PAGE MAY ASK AGAIN.
 *
 * NOT A UX NUMBER. The server rate-limits this endpoint to 10 requests per order per
 * minute (PAYMENT_STATUS_MAX_PER_ORDER_PER_MINUTE), and it is a WRITE path: a pending
 * order reconciles against the payment provider on every call. Polling every two or
 * three seconds would exhaust that budget in half a minute and start collecting 429s,
 * which is a worse page than the one this replaces.
 *
 * Eight seconds is roughly seven or eight requests a minute, which leaves headroom for
 * the request made on mount and for a customer who also presses "Check again".
 */
const POLL_INTERVAL_MS = 8_000;

/**
 * How long to keep asking.
 *
 * An ACH payment can take far longer than this to settle, so the window is not a
 * promise that it will be confirmed within it. It is the point at which watching stops
 * being useful and the customer is better served by the email, or by coming back later.
 * The manual button remains, and nothing about the order depends on this page.
 */
const POLL_MAX_MS = 90_000;

interface Presentation {
  /** The heading. Only PAID may read as success. */
  title: string;
  body: string;
  tone: 'success' | 'pending' | 'danger' | 'info';
  /** Whether pressing "Check again" could plausibly change the answer. */
  recheckable: boolean;
}

/**
 * One presentation per server status. MAP, NEVER INVENT.
 *
 * Every entry corresponds to exactly one value the backend can return. Nothing is
 * softened into sounding settled, and nothing implies a payment that has not been
 * recorded. The provider's own vocabulary — refunded, disputed, chargeback — never
 * appears: it is accurate and it is alarming, and a customer cannot act on it. They are
 * told the order needs review and how to reach us, which is the part they can act on.
 */
const PRESENTATION: Record<PaymentStatus, Presentation> = {
  paid: {
    title: 'Payment confirmed',
    body: 'Thank you. We have your payment and your order is confirmed. You can safely close this page.',
    tone: 'success',
    recheckable: false,
  },

  processing: {
    title: "We're confirming your payment",
    body: 'Your payment is going through. This usually takes a moment, and can take a little longer for bank transfers. You can safely close this page — we will email you as soon as it is confirmed.',
    tone: 'pending',
    recheckable: true,
  },

  // Our own integration could not get a usable answer. The customer is told the truth
  // that matters to them — it is not settled yet — and nothing about our plumbing.
  confirming_payment: {
    title: "We're confirming your payment",
    body: 'We have not been able to confirm your payment just yet. This is usually a short delay on our side rather than a problem with your payment. You can safely close this page — we will email you as soon as it is confirmed.',
    tone: 'pending',
    recheckable: true,
  },

  awaiting_payment: {
    title: 'No payment received yet',
    body: 'We have not received a payment for this order. If you did not finish checkout, your order is still saved and nothing has been charged.',
    tone: 'info',
    recheckable: true,
  },

  failed: {
    title: 'Payment was not completed',
    body: 'Your payment did not go through and nothing has been charged. You can start again whenever you are ready.',
    tone: 'danger',
    recheckable: false,
  },

  expired: {
    title: 'This checkout expired',
    body: 'This order was not paid in time and the checkout has expired. Nothing has been charged. You are welcome to place a new order.',
    tone: 'info',
    recheckable: false,
  },

  refunded: {
    title: 'This order was refunded',
    body: 'This order has been refunded. If you have any questions about it, please contact support.',
    tone: 'info',
    recheckable: false,
  },

  /*
   * REFUNDS, DISPUTES, CHARGEBACKS AND AMOUNT MISMATCHES ALL ARRIVE HERE.
   *
   * Distinct wording matters: it must not claim success, and it must not tell a customer
   * their payment failed when money may well have left their account. It says what is
   * true of all of them — somebody is looking at it — and points at support.
   */
  needs_review: {
    title: 'This order needs a closer look',
    body: 'There is something about this payment we need to check before confirming your order. Nothing further is needed from you right now — please contact support if you would like an update.',
    tone: 'info',
    recheckable: false,
  },
};

const NOTE_CLASS: Record<Presentation['tone'], string> = {
  success: 'mm-note mm-note--success',
  pending: 'mm-note mm-note--pending',
  danger: 'mm-note mm-note--danger',
  info: 'mm-note mm-note--info',
};

function PaymentReturnContent() {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  // Decides only what we OFFER, never what may be read -- the order API authorises itself.
  const { state: session } = useSession();

  const [status, setStatus] = useState<PaymentStatusResponse | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [failedToLoad, setFailedToLoad] = useState(false);
  const [checking, setChecking] = useState(true);

  /**
   * Ask once.
   *
   * A FAILURE HERE IS NEVER A PAYMENT FAILURE. If our own endpoint is unreachable we know
   * nothing about the payment, so the page says exactly that rather than showing the
   * customer a failed payment we have no evidence of.
   */
  const check = useCallback(
    async (signal?: AbortSignal, { background = false } = {}) => {
      if (!orderNumber) return;

      /*
       * A BACKGROUND CHECK IS SILENT, IN BOTH DIRECTIONS.
       *
       * It does not show the button as "Checking…", because a spinner blinking every
       * eight seconds reads as instability rather than progress. And it does not report
       * its own failure: one refused poll says nothing about the payment, and replacing
       * a calm "we are confirming" screen with an error would be actively misleading.
       * The next poll, or the manual button, simply tries again.
       */
      if (!background) {
        setChecking(true);
        setFailedToLoad(false);
      }

      try {
        const result = await fetchPaymentStatus(orderNumber, signal);
        if (signal?.aborted) return;
        setStatus(result);
      } catch (caught: unknown) {
        if (signal?.aborted || background) return;
        if (apiErrorStatus(caught) === 404) setNotFound(true);
        else setFailedToLoad(true);
      } finally {
        if (!background && !signal?.aborted) setChecking(false);
      }
    },
    [orderNumber],
  );

  // Exactly once per mount. The dependency is the callback, which changes only when the
  // order number does — so this cannot become a loop.
  useEffect(() => {
    const controller = new AbortController();
    void check(controller.signal);
    return () => controller.abort();
  }, [check]);

  /*
   * ===========================================================================
   * WHILE THE ANSWER CAN STILL CHANGE, ASK AGAIN WITHOUT BEING ASKED.
   *
   * A customer returning from an ACH checkout landed on "We’re confirming your
   * payment" and stayed there, correct but frozen, until they thought to press a button.
   * The order was active within seconds and the page had no way of knowing.
   *
   * `recheckable` is the existing authority for "could pressing the button change this
   * answer" — the same flag that decides whether the button is offered at all. Reusing
   * it means there is no second opinion about which states are still open, and a state
   * added later is covered by having its presentation filled in, not by editing a poll.
   *
   * THIS OBSERVES; IT NEVER DECIDES. Payment authority is the signed webhook and the
   * server-side reconciliation behind this endpoint. The browser reads what MM already
   * believes and can no more mark an order paid than the manual button can.
   *
   * The dependency is the STATUS STRING, not the response object. A new object arrives
   * on every poll, so depending on it would restart the effect each time and reset the
   * window, and the poll would run forever.
   */
  const currentStatus = status?.status;

  useEffect(() => {
    if (!currentStatus) return undefined;
    if (!PRESENTATION[currentStatus]?.recheckable) return undefined;

    const controller = new AbortController();
    const startedAt = Date.now();

    const timer = setInterval(() => {
      // Stops itself rather than relying on the status changing.
      if (Date.now() - startedAt >= POLL_MAX_MS) {
        clearInterval(timer);
        return;
      }
      void check(controller.signal, { background: true });
    }, POLL_INTERVAL_MS);

    /*
     * Cleared on unmount AND whenever the status changes: a settled order stops the
     * poll because its presentation is no longer recheckable, and a customer who
     * navigates away leaves no timer and no in-flight request behind.
     */
    return () => {
      clearInterval(timer);
      controller.abort();
    };
  }, [currentStatus, check]);

  if (notFound) {
    return (
      <div data-testid="return-not-found">
        <h1 className="mm-display">Order not found</h1>
        <p className="mm-lede mt-4">We couldn&rsquo;t find an order with that reference.</p>
        <Link to="/" className="mm-btn-secondary mt-6 inline-flex">
          Back to Metered Mining
        </Link>
      </div>
    );
  }

  if (!status) {
    // Either the first request is still in flight, or it failed outright. Neither is a
    // statement about the payment.
    return failedToLoad ? (
      <div data-testid="return-unavailable">
        <p className="mm-eyebrow">Payment</p>
        <h1 className="mm-display mt-3">We couldn&rsquo;t check your payment</h1>
        <p className="mm-lede mt-4">
          We couldn&rsquo;t reach our systems to check this order just now. This does not
          mean your payment failed. Please try again in a moment.
        </p>
        <button
          type="button"
          onClick={() => void check()}
          disabled={checking}
          data-testid="check-again"
          className="mm-btn-primary mt-8 inline-flex"
        >
          {checking ? 'Checking…' : 'Check again'}
        </button>
      </div>
    ) : (
      <p className="mm-help" role="status" data-testid="return-loading">
        Checking your payment…
      </p>
    );
  }

  const presentation = PRESENTATION[status.status];

  /*
   * PAID IS NOT THE SAME AS RUNNING, and this page must never say otherwise.
   *
   * Payment and provisioning are separate machines. A customer can be paid while their
   * hashrate is still being allocated, and telling them "your order is active" at that
   * moment would be a claim we cannot support. So the headline is chosen from BOTH: the
   * payment verdict decides whether we celebrate at all, and `fulfilment` decides whether
   * we may say the rental is live.
   *
   * `preparing` deliberately covers a failed provisioning too -- the server never tells
   * this page the difference, because the customer cannot act on it and the money is
   * safe either way. See paymentStatusService.FULFILMENT.
   */
  const isPaid = status.status === 'paid';
  const isActive = isPaid && status.fulfilment === 'active';
  const isEnded = isPaid && status.fulfilment === 'ended';
  /*
   * `preparing` is matched EXPLICITLY rather than inferred as "paid but not active",
   * so a finished rental can never fall through to allocation copy. That fall-through was
   * real: an ended order read as though its hashrate were still being set up.
   */
  const isPreparing = isPaid && status.fulfilment === 'preparing';

  const eyebrow = isEnded ? 'Order complete' : isPaid ? 'Payment confirmed' : 'Payment';

  const title = isActive
    ? 'Your order is active'
    : isEnded
      ? 'Your mining term has ended'
      : isPreparing
        ? 'Payment confirmed'
        : presentation.title;

  const body = isActive
    ? 'We\u2019ve received your payment and your hashrate is now mining.'
    : isEnded
      ? 'Your hashrate rental has completed its scheduled term.'
      : isPreparing
        ? /*
             SIDNEY'S WORDING (feedback #10), and it is truthful for every state it
             covers. The preparing state is paid-and-not-yet-active: not_provisioned,
             provisioning, and a provision_failed an operator is retrying. From the
             customer's side those are one situation -- the money is safe, the hashrate
             is not mining yet, and it is expected to.

             IT CANNOT APPEAR ONCE THE RENTAL IS LIVE. isActive is matched first and
             keeps "is now mining", so this sentence never survives PowerPool activating
             the allocation -- which is the failure the state split exists to prevent.
          */
          'We\u2019ve received your payment and your hashrate will begin mining soon.'
        : presentation.body;

  return (
    <>
      <p className="mm-eyebrow">{eyebrow}</p>
      <h1 className="mm-display mt-3" data-testid="return-title">
        {title}
      </h1>

      <p
        role="status"
        className={`${NOTE_CLASS[presentation.tone]} mt-6`}
        data-testid={`return-status-${status.status}`}
      >
        <span>{body}</span>
      </p>

      {/*
        ONCE PAID, THE CUSTOMER GETS THE RENTAL, NOT JUST A RECEIPT LINE.

        The same information as the activation email: what they bought, when it runs, where
        rewards go, what they paid. Before payment settles there is nothing to describe, so
        the sparse order/total pair remains -- a summary of an unpaid order would be
        describing something that has not happened.
      */}
      {isPaid ? (
        <ActivationSummary status={status} />
      ) : (
        <dl className="mm-panel-raised mt-8 grid gap-3 px-6 py-5" data-testid="return-order">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <dt className="mm-help">Order</dt>
            <dd className="mm-numeric m-0 font-semibold" data-testid="return-order-number">
              {status.orderNumber}
            </dd>
          </div>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <dt className="mm-help">Order total</dt>
            <dd className="mm-numeric m-0 font-semibold" data-testid="return-total">
              {formatCents(status.totalCents, status.currency)}
            </dd>
          </div>
        </dl>
      )}

      {/*
        A QUIET SIGN THAT SOMETHING IS ACTUALLY HAPPENING.

        The page re-checks by itself every few seconds, but nothing on screen said so, so
        a customer watching a static page had no way to tell it apart from a stuck one.
        This is the smallest honest indication that waiting is the right thing to do.

        GATED ON THE SAME RULE AS THE POLL AND THE BUTTON. `recheckable` is what decides
        whether the answer can still change, so this cannot outlive the waiting: the
        moment the order settles, it is gone without anything having to remove it.

        THE SPINNER IS DECORATIVE. It carries no information the sentence beside it does
        not, so it is hidden from assistive technology rather than announced. The status
        note above is already a live region; a second one would talk over it.

        It also stops spinning for anyone who has asked for reduced motion, and occupies
        a fixed box so nothing reflows around it.
      */}
      {presentation.recheckable && (
        <p className="mm-help mt-6 flex items-center gap-2" data-testid="return-waiting">
          <span
            aria-hidden="true"
            data-testid="return-spinner"
            className="inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-60 motion-reduce:animate-none"
          />
          Please wait while we confirm your payment.
        </p>
      )}

      {/*
        NO "CHECK AGAIN" HERE ANY MORE.

        The page re-reads the status by itself every few seconds, so the button asked the
        customer to do something already being done for them -- and its presence implied
        the opposite, that nothing would happen unless they acted.

        WHAT REPLACES IT IS NOTHING, deliberately. Once the automatic window closes the
        page still says what it said: the payment is being confirmed, an email is coming,
        and the page is safe to close. "View your order" below remains the way back to a
        record that is always current. A second action here would only offer a customer
        another way to wait.

        The retry on the LOAD-FAILURE branch above is a different thing and stays: there,
        we could not reach our own endpoint at all, and there is no automatic poll to
        recover it.
      */}
      <div className="mt-8 flex flex-wrap gap-3">
        {/*
          ONCE THE HASHRATE IS RUNNING, WATCHING IT IS THE POINT.
          ================================================================
          After activation the customer's next action is seeing their mining, not opening
          an account page -- so the provider link LEADS and the account link follows it as
          the secondary action. Before activation there is nothing to watch; after the term
          ends there is nothing running. It appears in neither.

          THE SERVER DECIDES WHETHER IT MAY APPEAR AT ALL. `watchUrl` is null in mock mode
          and null for anyone who is not the signed-in owner: a Solo watch URL embeds the
          full payout address this page otherwise masks. The browser never constructs this
          value, never rebuilds it from the address or the team, and never inspects it for
          "example.invalid" -- POWERPOOL_MODE remains the single authority, applied once in
          `customerFacingWatchUrl`.

          With no URL there is NO BUTTON -- not a disabled one, not a placeholder.
        */}
        {isActive && status.watchUrl && (
          <a
            href={status.watchUrl}
            target="_blank"
            // noreferrer keeps the provider from seeing where the customer came from.
            rel="noopener noreferrer"
            // Compact on desktop, full width on a phone. Same treatment as the
            // checkout CTA; see the note on the order page.
            className="mm-btn-primary inline-flex items-center justify-center sm:w-auto"
            data-testid="watch-live"
          >
            Watch your mining live
          </a>
        )}

        {status.status === 'failed' || status.status === 'expired' ? (
          <Link to="/" className="mm-btn-primary inline-flex" data-testid="return-restart">
            Configure a new order
          </Link>
        ) : (
          /*
           * ORDER DETAILS IS SESSION-SCOPED; THIS PAGE IS NOT.
           *
           * Checkout never asks anyone to sign in, so the ordinary customer arrives here
           * anonymous. Offering "View order details" unconditionally sent them to a page
           * that can only answer "sign in" or, if they happen to be signed in as another
           * address, "not found on your account".
           *
           * Neither of those is an authorisation bug -- the order page is ownership-scoped
           * and must stay that way. The bug was promising a destination we knew they could
           * not open. So the offer now matches what the session can actually deliver.
           */
          <Link
            to={session.status === 'authenticated' ? `/orders/${encodeURIComponent(status.orderNumber)}` : '/sign-in'}
            className="mm-btn-secondary inline-flex"
            data-testid="return-order-link"
          >
            {session.status === 'authenticated' ? 'View your order' : 'Sign in to view this order'}
          </Link>
        )}
      </div>

      {/*
        TWO EMAILS NOW EXIST, AND THEY COME FROM DIFFERENT SENDERS.

        Only claimed once the rental is active, because that is the only moment the
        Metered Mining activation email is sent -- promising it while provisioning is still
        running would promise a message that may not arrive for minutes, or at all.
      */}
      {isActive && (
        <p className="mm-help mt-6" data-testid="return-email-note">
          We&rsquo;ve sent your Metered Mining order confirmation by email. Your payment
          receipt is sent separately by our payment provider.
        </p>
      )}
      {isPreparing && (
        <p className="mm-help mt-6" data-testid="return-email-note">
          We&rsquo;ll email your order confirmation as soon as your hashrate is running. Your
          payment receipt is sent separately by our payment provider.
        </p>
      )}

      {/*
        THE RE-CHECK NOTICE IS GONE WITH THE BUTTON THAT CAUSED IT.

        `failedToLoad` is only ever set by a NON-background check, and the mount read is
        now the only one of those. A mount failure has no status to sit beside, so it
        renders the load-failure screen above instead. Automatic polls are silent by
        design: one refused poll says nothing about the payment.
      */}
    </>
  );
}

export default function PaymentReturn() {
  return (
    <PageShell>
      <PaymentReturnContent />
    </PageShell>
  );
}
